import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Rocket, CheckCircle2, XCircle, Clock, RotateCcw, Ban, RefreshCw,
    Plus, X, ChevronDown, ChevronRight, Server, GitCommit, ExternalLink, Trash2
} from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

interface DeploymentsPanelProps {
    /** When set, the panel is scoped to a single project (the project tab). */
    projectId?: string;
    /** Hide the project column when it is always the same project. */
    showProjectColumn?: boolean;
}

const STATUS_META: Record<string, { label: string; cls: string; Icon: any }> = {
    success: { label: 'Success', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
    failed: { label: 'Failed', cls: 'bg-red-50 text-red-700 border-red-200', Icon: XCircle },
    running: { label: 'Running', cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Clock },
    queued: { label: 'Queued', cls: 'bg-gray-50 text-gray-600 border-gray-200', Icon: Clock },
    rolled_back: { label: 'Rolled back', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: RotateCcw },
    cancelled: { label: 'Cancelled', cls: 'bg-gray-50 text-gray-500 border-gray-200', Icon: Ban },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const meta = STATUS_META[status] || { label: status, cls: 'bg-gray-50 text-gray-600 border-gray-200', Icon: Clock };
    const { Icon } = meta;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${meta.cls}`}>
            <Icon className="w-3 h-3" />
            {meta.label}
        </span>
    );
};

const fmtDuration = (seconds: number | null | undefined): string => {
    if (seconds === null || seconds === undefined) return '—';
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const fmtWhen = (value: string | null | undefined): string => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 8) return `${days}d ago`;
    return d.toLocaleDateString();
};

export const DeploymentsPanel: React.FC<DeploymentsPanelProps> = ({ projectId, showProjectColumn = true }) => {
    const { user } = useAuth();
    const [deployments, setDeployments] = useState<any[]>([]);
    const [stats, setStats] = useState<any | null>(null);
    const [environments, setEnvironments] = useState<any[]>([]);
    const [projects, setProjects] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [showLogModal, setShowLogModal] = useState(false);

    const [filters, setFilters] = useState<{ environment: string; status: string; days: number }>({
        environment: '',
        status: '',
        days: 30,
    });

    const isAdmin = useMemo(() => {
        const groups: string[] = (user as any)?.groups || [];
        return groups.some((g) => ['jira-administrators', 'administrators', 'admin'].includes(String(g).toLowerCase()));
    }, [user]);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const query: any = { days: filters.days, limit: 100 };
            if (projectId) query.project_id = projectId;
            if (filters.environment) query.environment = filters.environment;
            if (filters.status) query.status = filters.status;

            const statsQuery: any = { days: filters.days };
            if (projectId) statsQuery.project_id = projectId;
            if (filters.environment) statsQuery.environment = filters.environment;

            const [rows, st, envs] = await Promise.all([
                apiService.getDeployments(query),
                apiService.getDeploymentStats(statsQuery),
                apiService.getDeploymentEnvironments(projectId),
            ]);
            setDeployments(Array.isArray(rows) ? rows : []);
            setStats(st || null);
            setEnvironments(Array.isArray(envs) ? envs : []);
        } catch (e: any) {
            console.error('Failed to load deployments:', e);
            setError(e?.message || 'Failed to load deployments');
        } finally {
            setIsLoading(false);
        }
    }, [projectId, filters.environment, filters.status, filters.days]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (showProjectColumn && !projectId) {
            apiService.getProjects()
                .then((p: any) => setProjects(Array.isArray(p) ? p : []))
                .catch(() => setProjects([]));
        }
    }, [showProjectColumn, projectId]);

    const envOptions = useMemo(() => environments.map((e) => e.environment), [environments]);

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this deployment record? This does not affect anything that was deployed.')) return;
        try {
            await apiService.deleteDeployment(id);
            load();
        } catch (e) {
            console.error('Failed to delete deployment:', e);
        }
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-blue-600" />
                    <h2 className="text-base font-semibold text-gray-900">Deployments</h2>
                    <span className="text-xs text-gray-500">
                        {isLoading ? 'loading…' : `${deployments.length} in the last ${filters.days} days`}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowLogModal(true)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Log deployment
                    </button>
                </div>
            </div>

            {error && (
                <div className="px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg">
                    {error}
                </div>
            )}

            {/* Environment status strip */}
            {environments.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {environments.map((env) => (
                        <div key={env.environment} className="p-3 bg-white border border-gray-200 rounded-xl">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-900 uppercase tracking-wide">
                                    <Server className="w-3.5 h-3.5 text-gray-400" />
                                    {env.environment}
                                </span>
                                <StatusBadge status={env.last_deployment?.status} />
                            </div>
                            <div className="text-xs text-gray-500">
                                {fmtWhen(env.last_deployment?.started_at)}
                                {env.last_deployment?.duration_seconds != null && ` · ${fmtDuration(env.last_deployment.duration_seconds)}`}
                            </div>
                            {env.last_deployment?.target && (
                                <div className="mt-1 text-xs text-gray-600 truncate" title={env.last_deployment.target}>
                                    {env.last_deployment.target}
                                </div>
                            )}
                            <div className="mt-1 text-[11px] text-gray-400">
                                {env.total_deployments} deployment{env.total_deployments === 1 ? '' : 's'} tracked
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Stats */}
            {stats && stats.total > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                        { label: 'Deployments', value: stats.total, hint: `${stats.deployments_per_day}/day` },
                        { label: 'Success rate', value: stats.success_rate != null ? `${stats.success_rate}%` : '—', hint: `${stats.succeeded} ok · ${stats.failed} failed` },
                        { label: 'Change failure', value: stats.change_failure_rate != null ? `${stats.change_failure_rate}%` : '—', hint: 'failed + rolled back' },
                        { label: 'Avg duration', value: fmtDuration(stats.avg_duration_seconds), hint: 'successful only' },
                        { label: 'Time to restore', value: stats.mean_time_to_restore_minutes != null ? `${stats.mean_time_to_restore_minutes}m` : '—', hint: 'failure → next success' },
                    ].map((card) => (
                        <div key={card.label} className="p-3 bg-white border border-gray-200 rounded-xl">
                            <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{card.label}</div>
                            <div className="mt-0.5 text-lg font-semibold text-gray-900">{card.value}</div>
                            <div className="text-[11px] text-gray-400">{card.hint}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
                <select
                    value={filters.environment}
                    onChange={(e) => setFilters({ ...filters, environment: e.target.value })}
                    className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-700"
                >
                    <option value="">All environments</option>
                    {envOptions.map((e) => <option key={e} value={e}>{e}</option>)}
                </select>
                <select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-700"
                >
                    <option value="">All statuses</option>
                    {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                </select>
                <select
                    value={filters.days}
                    onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })}
                    className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-700"
                >
                    {[7, 14, 30, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
                </select>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {isLoading && deployments.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-500">Loading deployments…</div>
                ) : deployments.length === 0 ? (
                    <div className="p-8 text-center">
                        <Rocket className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-700">No deployments tracked yet</p>
                        <p className="mt-1 text-xs text-gray-500 max-w-md mx-auto">
                            Pipelines report themselves with <code className="px-1 bg-gray-100 rounded">report-deploy.sh</code>.
                            You can also log one by hand with the button above.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr className="text-left text-gray-500">
                                    <th className="px-3 py-2 font-medium w-6"></th>
                                    <th className="px-3 py-2 font-medium">When</th>
                                    {showProjectColumn && !projectId && <th className="px-3 py-2 font-medium">Project</th>}
                                    <th className="px-3 py-2 font-medium">Environment</th>
                                    <th className="px-3 py-2 font-medium">Status</th>
                                    <th className="px-3 py-2 font-medium">Version / commit</th>
                                    <th className="px-3 py-2 font-medium">Target</th>
                                    <th className="px-3 py-2 font-medium">Triggered by</th>
                                    <th className="px-3 py-2 font-medium">Duration</th>
                                    <th className="px-3 py-2 font-medium">Shipped</th>
                                    {isAdmin && <th className="px-3 py-2 font-medium w-8"></th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {deployments.map((d) => {
                                    const isOpen = expanded === d.id;
                                    return (
                                        <React.Fragment key={d.id}>
                                            <tr className="hover:bg-gray-50">
                                                <td className="px-3 py-2">
                                                    <button onClick={() => setExpanded(isOpen ? null : d.id)} className="text-gray-400 hover:text-gray-600">
                                                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2 text-gray-700 whitespace-nowrap" title={d.started_at || ''}>
                                                    {fmtWhen(d.started_at)}
                                                </td>
                                                {showProjectColumn && !projectId && (
                                                    <td className="px-3 py-2">
                                                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded font-medium">
                                                            {d.project?.key || '—'}
                                                        </span>
                                                    </td>
                                                )}
                                                <td className="px-3 py-2 text-gray-700">{d.environment}</td>
                                                <td className="px-3 py-2"><StatusBadge status={d.status} /></td>
                                                <td className="px-3 py-2 text-gray-600 font-mono">
                                                    {d.version || (d.commit_sha ? d.commit_sha.slice(0, 8) : '—')}
                                                </td>
                                                <td className="px-3 py-2 text-gray-600 max-w-[220px] truncate" title={d.target || ''}>
                                                    {d.target || '—'}
                                                </td>
                                                <td className="px-3 py-2 text-gray-600">
                                                    {d.triggered_by_user?.display_name || d.triggered_by_label || d.triggered_by_source}
                                                    <span className="ml-1 text-gray-400">({d.triggered_by_source})</span>
                                                </td>
                                                <td className="px-3 py-2 text-gray-600">{fmtDuration(d.duration_seconds)}</td>
                                                <td className="px-3 py-2">
                                                    {d.issues?.length ? (
                                                        <span className="text-blue-600 font-medium">{d.issues.length} issue{d.issues.length === 1 ? '' : 's'}</span>
                                                    ) : <span className="text-gray-400">—</span>}
                                                </td>
                                                {isAdmin && (
                                                    <td className="px-3 py-2">
                                                        <button onClick={() => handleDelete(d.id)} className="text-gray-300 hover:text-red-600" title="Delete record">
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                            {isOpen && (
                                                <tr className="bg-gray-50">
                                                    <td colSpan={11} className="px-6 py-3">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            <div className="space-y-1.5 text-xs">
                                                                {d.commit_message && (
                                                                    <div className="flex items-start gap-1.5">
                                                                        <GitCommit className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                                                                        <span className="text-gray-700">{d.commit_message}</span>
                                                                    </div>
                                                                )}
                                                                {d.repository && <div className="text-gray-500">Repository: <span className="text-gray-700">{d.repository}</span>{d.branch ? ` (${d.branch})` : ''}</div>}
                                                                {d.commit_sha && <div className="text-gray-500 font-mono">Commit: {d.commit_sha}</div>}
                                                                {d.finished_at && <div className="text-gray-500">Finished: {new Date(d.finished_at).toLocaleString()}</div>}
                                                                {d.notes && <div className="text-gray-500">Notes: <span className="text-gray-700">{d.notes}</span></div>}
                                                                {d.log_url && (
                                                                    <a href={d.log_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                                                                        <ExternalLink className="w-3 h-3" /> Open build log
                                                                    </a>
                                                                )}
                                                                {d.issues?.length > 0 && (
                                                                    <div className="pt-1">
                                                                        <div className="text-gray-500 mb-1">Issues shipped:</div>
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {d.issues.map((i: any) => (
                                                                                <a key={i.id} href={`/issues/${i.id}`} className="px-1.5 py-0.5 bg-white border border-gray-200 rounded font-medium text-blue-600 hover:bg-blue-50" title={i.summary}>
                                                                                    {i.key}
                                                                                </a>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {d.log_tail && (
                                                                <div>
                                                                    <div className="text-xs text-gray-500 mb-1">Log tail</div>
                                                                    <pre className="p-2 bg-gray-900 text-gray-100 rounded-lg text-[11px] leading-relaxed overflow-x-auto max-h-48 whitespace-pre-wrap">{d.log_tail}</pre>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showLogModal && (
                <LogDeploymentModal
                    projectId={projectId}
                    projects={projects}
                    environments={envOptions}
                    onClose={() => setShowLogModal(false)}
                    onSaved={() => { setShowLogModal(false); load(); }}
                />
            )}
        </div>
    );
};

interface LogModalProps {
    projectId?: string;
    projects: any[];
    environments: string[];
    onClose: () => void;
    onSaved: () => void;
}

const LogDeploymentModal: React.FC<LogModalProps> = ({ projectId, projects, environments, onClose, onSaved }) => {
    const [form, setForm] = useState<any>({
        project_id: projectId || '',
        environment: environments[0] || 'production',
        status: 'success',
        version: '',
        target: '',
        commit_sha: '',
        commit_message: '',
        notes: '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErr(null);
        if (!form.project_id) { setErr('Please choose a project'); return; }
        setSaving(true);
        try {
            const payload: any = { ...form, triggered_by_source: 'manual' };
            Object.keys(payload).forEach((k) => { if (payload[k] === '') delete payload[k]; });
            payload.project_id = form.project_id;
            payload.environment = form.environment;
            payload.status = form.status;
            await apiService.createDeployment(payload);
            onSaved();
        } catch (e: any) {
            setErr(e?.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-900">Log a deployment</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="px-5 py-4 space-y-3">
                    <p className="text-xs text-gray-500">
                        For releases that are not driven by a pipeline — manual promotions, WordPress releases,
                        or filling in something that happened earlier.
                    </p>

                    {!projectId && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Project *</label>
                            <select
                                value={form.project_id}
                                onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg"
                            >
                                <option value="">Select a project…</option>
                                {projects.map((p: any) => <option key={p.id} value={p.id}>{p.key} — {p.name}</option>)}
                            </select>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Environment *</label>
                            <input
                                list="deploy-env-options"
                                value={form.environment}
                                onChange={(e) => setForm({ ...form, environment: e.target.value })}
                                placeholder="production"
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg"
                            />
                            <datalist id="deploy-env-options">
                                {['production', 'staging', 'preprod', ...environments].map((e) => <option key={e} value={e} />)}
                            </datalist>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Status *</label>
                            <select
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg"
                            >
                                {['success', 'failed', 'running', 'rolled_back', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Version / tag</label>
                            <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })}
                                placeholder="v1.4.2" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Commit sha</label>
                            <input value={form.commit_sha} onChange={(e) => setForm({ ...form, commit_sha: e.target.value })}
                                placeholder="optional" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg font-mono" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Target</label>
                        <input value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })}
                            placeholder="16.16.101.97 / pm2:planner" className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">What shipped</label>
                        <textarea value={form.commit_message} onChange={(e) => setForm({ ...form, commit_message: e.target.value })}
                            rows={2} placeholder="Mention issue keys (e.g. TES-14) and they are linked automatically"
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg" />
                    </div>

                    {err && <div className="px-2.5 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg">{err}</div>}

                    <div className="flex items-center justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                            {saving ? 'Saving…' : 'Save deployment'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
